import {
  GraphComparisonApiService,
  GraphLegacyApiService,
} from '@kizi/comparator-core';
import { useCallback } from 'react';

export const extractAddressInfo = (data) => {
  const addressInfo = {
    city: '',
    housenumber: '',
    housenumberaddition: '',
    postcode: '',
    street: '',
  };

  data.forEach((item) => {
    switch (item.name) {
      case 'city':
        addressInfo.city = item.value;
        break;
      case 'housenumber':
        addressInfo.housenumber = item.value;
        break;
      case 'housenumberaddition':
        addressInfo.housenumberaddition = item.value;
        break;
      case 'postcode':
        addressInfo.postcode = item.value;
        break;
      case 'street':
        addressInfo.street = item.value;
        break;
      default:
        break;
    }
  });
  return addressInfo;
};

export const findValueByName = (name, data) => {
  const item = data.find((item) => item.name === name);
  return item ? item.value : null;
};

export const solarPanelOrderDataBuilder = (state) => {
  const roofLocationMapping = {
    zuiden: 'V_SOUTH',
    zuidoost: 'V_SOUTHEAST',
    zuidwest: 'V_SOUTHWEST',
    westen: 'V_WEST',
    oosten: 'V_EAST',
    noorden: 'V_NORTH',
    noordoost: 'V_NORTHEAST',
    noordwest: 'V_NORTHWEST',
  };

  const roofLocationPowerFactorMapping = {
    zuiden: 0.95,
    zuidoost: 0.87,
    zuidwest: 0.9,
    westen: 0.82,
    oosten: 0.8,
    noorden: 0.6,
    noordoost: 0.7,
    noordwest: 0.7,
  };

  const getUserInputStep = state.context.initialSteps[1].fields;
  const getHouseInputStep = state.context.initialSteps[2].fields;
  const solarPanelsStep = state.context.initialSteps[3].fields;

  const { city, housenumber, housenumberaddition, postcode, street } =
    extractAddressInfo(getUserInputStep);
  const roofType = findValueByName('roofType', solarPanelsStep);
  const roofLocation = findValueByName('roofLocation', getHouseInputStep);
  const usageElectricity = findValueByName(
    'electricityusage',
    getUserInputStep,
  );
  return {
    objectBuildings: {
      addresses: {
        city,
        houseNumber: Number(housenumber),
        houseNumberAddition: housenumberaddition,
        postalCode: postcode,
        street,
      },
      numberOfSolarPanels: findValueByName(
        'numberOfSolarPanels',
        solarPanelsStep,
      ),
      roofConstructionType: roofType === 'platdak' ? 'V02' : 'V01',
      roofOrientation:
        roofType === 'platdak' ? null : roofLocationMapping[roofLocation],
    },
    salesDockComparisons: {
      'powerFactor':
        roofType === 'platdak'
          ? 1
          : roofLocationPowerFactorMapping[roofLocation],
      usageElectricity: Number(usageElectricity),
    },
  };
};

export const restructurePolicyDetails = (
  jsonObjectBuilderData,
  shouldReplaceFuseBox,
  roofSurface,
) => {
  const { salesDockProductOptionInputs } = jsonObjectBuilderData;
  return {
    salesDockProductOptionInputs: [
      {
        extraHighRoof: salesDockProductOptionInputs.extraHighRoof,
        specialPlace: salesDockProductOptionInputs.specialPlace,
        removeNumberOfPanels: salesDockProductOptionInputs.removeNumberOfPanels,
        severalRoofSurfacesCount: Number(roofSurface) - 1,
        replaceFuseBox: shouldReplaceFuseBox,
      },
    ],
  };
};

function transformDateFormat(inputDate) {
  const parts = inputDate.split('-');

  if (parts.length !== 3) {
    return 'Invalid date format';
  }

  const day = parts[0];
  const month = parts[1];
  const year = parts[2];

  const transformedDate = `${year}-${month}-${day}`;

  return transformedDate;
}

export const handlePartyPolicyHolders = (data) => {
  return {
    partyPolicyHolders: {
      ...data,
      birthDate: transformDateFormat(data.birthDate),
    },
  };
};

export const handleTopThree3P = (products) => {
  const validProducts = products.filter(
    (product) => product && product?.tags !== null,
  );

  const notOrderableCount = validProducts.filter(
    (product) => product.orderType === 'NOT_ORDERABLE',
  ).length;

  if (notOrderableCount > 1) {
    return [];
  }

  const featuredProducts = validProducts.filter(
    (product) => product.tags === 'featured',
  );

  const productWithHighestDownloadSpeed = featuredProducts.reduce(
    (maxProduct, currentProduct) => {
      return currentProduct.downloadSpeedInMb >
        (maxProduct?.downloadSpeedInMb || 0)
        ? currentProduct
        : maxProduct;
    },
    null,
  );

  const remainingProducts = validProducts.filter(
    (product) => product.tags !== 'featured',
  );

  return [...remainingProducts, productWithHighestDownloadSpeed].slice(0, 3);
};

export const handleTopThreeCb3P = (products) => {
  const ordering = ['lowestPrice', 'highestRating', 'fastestSpeed'];

  const validProducts = products.filter(
    (product) => product && product?.tags.length > 0,
  );

  const notOrderableCount = validProducts.filter(
    (product) => product.orderType === 'NOT_ORDERABLE',
  ).length;

  if (notOrderableCount > 1) {
    return [];
  }

  const sortedProducts = [...validProducts].sort((a, b) => {
    return ordering.indexOf(a.tags[0]) - ordering.indexOf(b.tags[0]);
  });

  return sortedProducts;
};

const getProductByTag = (products, tag) =>
  products.find((product) => product.tags === tag);

export const handleTopThreeHealth = (products) => {
  const validProducts = products.filter(
    (product) => product && product?.tags !== null,
  );

  const lowestPriceProduct = getProductByTag(validProducts, 'lowestPrice');

  const moreHospitalChoice = getProductByTag(
    validProducts,
    'moreHospitalChoice',
  );
  const completeHospitalChoice = getProductByTag(
    validProducts,
    'completeHospitalChoice',
  );
  const highestScoreForPrice = getProductByTag(
    validProducts,
    'highestScoreForPrice',
  );
  const middleProduct =
    moreHospitalChoice || completeHospitalChoice || highestScoreForPrice;

  const sustainableChoice = getProductByTag(validProducts, 'sustainableChoice');
  const healthyLiving = getProductByTag(validProducts, 'healthyLiving');

  // check highestScoreForPrice for the right product, but it will only be used if it wasn't already used as middleProduct
  const rightProduct =
    sustainableChoice ||
    healthyLiving ||
    (middleProduct !== highestScoreForPrice ? highestScoreForPrice : null);

  const topThree = [lowestPriceProduct, middleProduct, rightProduct].filter(
    Boolean,
  );
  return topThree;
};

export function getRiskProductTestScore(
  coverageType,
  allRisk,
  supplierCustomAttributes,
) {
  let searchDescription;

  switch (coverageType) {
    case '1':
      searchDescription = allRisk
        ? 'Testscore Inboedel Allrisk'
        : 'Testscore Inboedel';
      break;
    case '2':
      searchDescription = allRisk
        ? 'Testscore Opstal Allrisk'
        : 'Testscore Opstal';
      break;
    case '3':
      searchDescription = allRisk
        ? 'Testscore Pakket Allrisk'
        : 'Testscore Pakket';
      break;
    default:
      throw new Error('Couldnt match the description');
  }

  const attribute = (supplierCustomAttributes || []).find(
    (attr) => attr.description === searchDescription,
  );

  if (!attribute) {
    throw new Error(
      `No matching test score found for description: ${searchDescription}`,
    );
  }

  return attribute.value;
}

export const usePeregrineEventHandler = (props, state, api) => {
  return useCallback(async () => {
    if (!props.registerInPeregrineEvent) return;

    const allFields = [
      ...(state.context.externalFields || []),
      ...state.context.fields,
    ];

    try {
      const graphApi = api as GraphComparisonApiService<GraphLegacyApiService>;
      const [userHash, partnerHash, activeExperiments] = await Promise.all([
        graphApi.getPeregrineUserHash(allFields),
        graphApi.getPeregrinePartnerHash(allFields),
        graphApi.getPeregrineActiveExperiments(allFields),
      ]);

      const eventData = props.registerInPeregrineEvent(
        allFields,
        userHash,
        partnerHash,
        activeExperiments,
      );
      await graphApi.sendPeregrineEvent(eventData);
    } catch (e) {
      console.error('Peregrine event error:', e);
    }
  }, [
    props.registerInPeregrineEvent,
    state.context.externalFields,
    state.context.fields,
    api,
  ]);
};
